import { getEmailPreferencesOptions, updateEmailPreferencesMutation } from '@/client/@tanstack/react-query.gen'
import type { EmailPreferenceResponse } from '@/client/types.gen'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { SettingsPageSkeleton } from './-components/skeleton'

const NOTIFICATION_HELPER_TEXT: Record<string, string> = {
  DEAL_PARTICIPANT_ASSIGNED: 'Get notified when you\'re added as a participant on a deal',
  WEEKLY_PLATFORM_ACTIVITY: 'Receive a weekly summary of your team\'s platform activity',
}

export const Route = createFileRoute('/_authed/_user/settings/email-notification-preferences')({
  component: NotificationsPage,
  pendingComponent: SettingsPageSkeleton,
  beforeLoad: () => ({
    getTitle: () => 'Notifications',
    getDescription: () => 'Manage email notification preferences',
  }),
})

function NotificationsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const {
    data: preferences,
    isLoading,
    error,
  } = useQuery({
    ...getEmailPreferencesOptions(),
  })

  const { mutateAsync: updatePreferences, isPending } = useMutation({
    ...updateEmailPreferencesMutation(),
    onSuccess: (data) => {
      queryClient.setQueryData(getEmailPreferencesOptions().queryKey, data)
    },
    onError: error_ =>
      toast({
        title: 'Failed to update preference',
        description: error_ instanceof Error ? error_.message : 'An unexpected error occurred',
        variant: 'destructive',
      }),
  })

  async function handleToggle(pref: EmailPreferenceResponse, enabled: boolean) {
    if (!preferences)
      return

    const updated = preferences.map(p => ({
      emailType: p.emailType,
      enabled: p.emailType === pref.emailType ? enabled : p.enabled,
    }))

    await updatePreferences({
      body: { preferences: updated },
    })
  }

  if (isLoading) {
    return <SettingsPageSkeleton />
  }

  return (
    <>
      {!!error && (
        <Alert variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-4">
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription className="mt-1.5">
            Control which email notifications you receive from SUPPLYCO.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {preferences && preferences.length > 0 ? (
            <div className="divide-y">
              {preferences.map(pref => (
                <div key={pref.emailType} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
                  <div className="space-y-0.5 pr-4">
                    <p className="text-sm font-medium leading-none">{pref.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {NOTIFICATION_HELPER_TEXT[pref.emailType] ?? `Manage your ${pref.description.toLowerCase()} notifications`}
                    </p>
                  </div>
                  <Switch
                    checked={pref.enabled}
                    disabled={isPending}
                    onCheckedChange={checked => handleToggle(pref, checked)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No configurable notifications available.</p>
          )}
        </CardContent>
      </Card>
    </>
  )
}
